import { PriorityHigh } from '@mui/icons-material'
import { Alert, Box, Paper, Stack } from '@mui/material'
import type { ReactNode } from 'react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import CustomTypography from '@/components/dataDisplay/CustomTypography'
import { useAlertContext } from '@/contexts/AlertContext'
import { ConfigReviewEditsDialog } from '@/features/controller/configuration/components/ConfigReviewEditsDialog'
import { ConfigVersionInfo } from '@/features/controller/configuration/components/ConfigVersionInfo'
import { ConfigVersionNavigationLinks } from '@/features/controller/configuration/components/ConfigVersionNavigationLinks'
import { TextEditor } from '@/features/controller/configuration/components/textEditor/TextEditor'
import { useControllerConfigurationFileContext } from '@/features/controller/configuration/contexts/ControllerConfigurationFileContext'
import type { ArrivingProposal } from '@/features/controller/configuration/hooks/useArrivingProposal'
import { useArrivingProposal } from '@/features/controller/configuration/hooks/useArrivingProposal'
import { useFetchNewerVersion } from '@/features/controller/configuration/hooks/useFetchNewerVersion'
import type { ControllerConfigVersion, ControllerConfigVersionUpdate } from '@/features/controller/configuration/types'
import { getControllerConfigType } from '@/features/controller/configuration/utils/controllerConfigTypes'
import { isSaveConflict } from '@/features/controller/configuration/utils/saveConflict'
import { validateConfigText } from '@/features/controller/configuration/utils/validateConfigText'
import { useControllerRestartStatusPolling } from '@/features/controller/restart/hooks/useControllerRestartStatusPolling'
import { useRestartControllerMutation } from '@/features/controller/restart/hooks/useRestartControllerMutation'
import { ControllerRestartMethod } from '@/features/controller/restart/types'
import CancelConfirmIconButtons from '@/features/site/components/CancelConfirmIconButtons'
import EditIconButton from '@/features/site/components/EditIconButton'

type ConfigVersionDisplayEditableProps = {
  configVersion: ControllerConfigVersion
  controllerId: string
  infoPanel: ReactNode
  schema?: object | null
}

type ConfigVersionMode =
  | { kind: 'viewing' }
  /**
   * `baseVersion`: the version the draft was started from. Saving is sent against it and the review diff
   * compares with it, so a version fetched meanwhile makes the save fail with a conflict instead of being
   * overwritten, and the diff shows only the user's changes.
   * `baseReplaced`: a save conflicted, so the base is now the version saved meanwhile, which the draft would replace.
   */
  | ({ kind: 'editing' } & Draft)
  | ({ kind: 'reviewing' } & Draft)

type Draft = {
  baseVersion: ControllerConfigVersion
  baseReplaced: boolean
  text: string
  comment: string
  fromProposal: boolean
}

function initialMode(arrivingProposal: ArrivingProposal, configVersion: ControllerConfigVersion): ConfigVersionMode {
  switch (arrivingProposal.kind) {
    case 'accepted':
      return {
        kind: 'editing',
        // The version shown is the base; if the proposal was made against an older one, the review says so
        baseVersion: configVersion,
        baseReplaced: arrivingProposal.baseReplaced,
        text: arrivingProposal.edit.value,
        comment: arrivingProposal.edit.comment ?? '',
        fromProposal: true,
      }
    case 'none':
      return { kind: 'viewing' }
  }
}

/**
 * Reusable component for displaying a config version with editing capability
 */
export function ConfigVersionDisplayEditable({
  configVersion,
  controllerId,
  infoPanel,
  schema,
}: Readonly<ConfigVersionDisplayEditableProps>) {
  const { pushAlert } = useAlertContext()
  const { t } = useTranslation()

  const { updateAndInvalidate, isUpdatePending } = useControllerConfigurationFileContext()
  const { fetchNewerVersion, isFetchingNewerVersion } = useFetchNewerVersion(controllerId)

  const arrivingProposal = useArrivingProposal(configVersion.id)
  const [mode, setMode] = useState(() => initialMode(arrivingProposal, configVersion))

  const [themeRefresh, setThemeRefresh] = useState(0)
  const [isSchemaValid, setIsSchemaValid] = useState(true)

  const configType = getControllerConfigType(configVersion.configurationTypeId)

  function enterEditMode() {
    setMode({
      kind: 'editing',
      baseVersion: configVersion,
      baseReplaced: false,
      text: configVersion.value,
      comment: '',
      fromProposal: false,
    })
  }

  function cancelEditMode() {
    setMode({ kind: 'viewing' })
  }

  function enterReviewMode() {
    // Review is only reachable from editing, where the confirm button is
    if (mode.kind !== 'editing') {
      return
    }
    const editedText = mode.text

    // Sync validation on submit
    const { isValid, errorMessage } = validateConfigText(configType, editedText, t)
    if (!isValid) {
      // Don't open review dialog and push alert message to user
      pushAlert({
        message: errorMessage!,
        severity: 'error',
      })
      return
    }

    setMode({ ...mode, kind: 'reviewing' })
  }

  function exitReviewMode() {
    if (mode.kind === 'reviewing') {
      setMode({ ...mode, kind: 'editing' })
    }
    setThemeRefresh(Date.now())
  }

  const { restartController, isPending: isRestartingController } = useRestartControllerMutation(
    controllerId,
    ControllerRestartMethod.SSH,
  )

  // Enable polling when restart is in progress
  useControllerRestartStatusPolling({
    controllerId,
    enabled: true,
  })

  async function onSave(update: ControllerConfigVersionUpdate) {
    try {
      await updateAndInvalidate(update)

      pushAlert({
        message: t('controllers.configuration.update_success'),
        severity: 'success',
      })
      setMode({ kind: 'viewing' })
      setThemeRefresh(Date.now())
      return true
    } catch (error) {
      if (isSaveConflict(error)) {
        await rebaseOnNewerVersion()
      } else {
        pushAlert({
          message: t('controllers.configuration.update_error'),
          severity: 'error',
        })
      }
      return false
    }
  }

  async function rebaseOnNewerVersion() {
    const newerVersion = await fetchNewerVersion(configVersion.configurationTypeId)
    if (!newerVersion) {
      pushAlert({
        message: t('controllers.configuration.update_error_conflict'),
        severity: 'error',
      })
      return
    }
    // The draft stays as it is; the review now shows what saving it would do to the newer version
    setMode((current) =>
      current.kind === 'viewing' ? current : { ...current, baseVersion: newerVersion, baseReplaced: true },
    )
  }

  async function onSaveAndRestart(update: ControllerConfigVersionUpdate) {
    const saveSucceeded = await onSave(update)
    if (saveSucceeded) {
      try {
        await restartController()
        // Don't show success immediately - the polling mechanism will handle status updates
        // The restart request was accepted, but actual restart success will be shown via RestartProgressSection
        pushAlert({
          message: t('controllers.configuration.restart_requested'),
          severity: 'info',
        })
      } catch {
        pushAlert({
          message: t('controllers.configuration.restart_error'),
          severity: 'error',
        })
        return false
      }
    }
  }

  // Outside edit mode, show the latest version as fetched, so a refetch after someone else's save is visible
  const currentText = mode.kind === 'viewing' ? configVersion.value : mode.text

  return (
    <Box my={2}>
      <Stack direction="row" gap={2}>
        {/* Main editor section */}
        <Box sx={{ flex: 3 }}>
          <Paper sx={{ p: 3 }}>
            {/* Optional title */}
            <CustomTypography sx={{ mb: 2 }} variant="h2">
              {configType.name}
            </CustomTypography>

            {/* Proposed edit info */}
            {mode.kind !== 'viewing' && mode.fromProposal && (
              <Stack direction="row" sx={{ mt: 2 }}>
                <PriorityHigh />
                <CustomTypography>{t('controllers.configuration.proposed_edit.proposal_info')}</CustomTypography>
              </Stack>
            )}

            {mode.kind !== 'viewing' && (mode.baseReplaced || mode.baseVersion.id !== configVersion.id) && (
              <Alert severity="warning" sx={{ mt: 2 }}>
                {t('controllers.configuration.newer_version_while_editing', { configTypeName: configType.name })}
              </Alert>
            )}

            {/* Edit controls */}
            <Stack direction="row" justifyContent="flex-end" sx={{ mb: 2 }}>
              {mode.kind !== 'viewing' ? (
                <CancelConfirmIconButtons
                  cancelDisabled={false}
                  disabled={!isSchemaValid}
                  onCancelClick={cancelEditMode}
                  onConfimClick={enterReviewMode}
                />
              ) : (
                <EditIconButton tooltipText="Edit configuration" onClick={enterEditMode} />
              )}
            </Stack>

            {/* Text Editor */}
            <TextEditor
              configTypeId={configType.id}
              language={configType.textEditorLanguage}
              refreshTrigger={themeRefresh}
              schema={schema}
              value={currentText}
              onChange={
                mode.kind === 'editing'
                  ? // Only while editing: @monaco-editor/react keeps the last handler subscribed after onChange
                    // becomes undefined, and calls it when the read-only editor's value changes.
                    // Monaco's OnChange allows undefined; the draft is always a string.
                    (value) =>
                      setMode((current) => (current.kind === 'editing' ? { ...current, text: value ?? '' } : current))
                  : undefined
              }
              onValidationChange={setIsSchemaValid}
            />

            {/* Review dialog (only for editable mode) */}
            <ConfigReviewEditsDialog
              baseReplaced={mode.kind !== 'viewing' && mode.baseReplaced}
              baseVersion={mode.kind === 'viewing' ? configVersion : mode.baseVersion}
              comment={mode.kind === 'viewing' ? '' : mode.comment}
              isFetchingNewerVersion={isFetchingNewerVersion}
              isUpdating={isUpdatePending || isRestartingController}
              language={configType.textEditorLanguage}
              modifiedText={currentText}
              open={mode.kind === 'reviewing'}
              onClose={exitReviewMode}
              onCommentChange={(comment) =>
                setMode((current) => (current.kind === 'reviewing' ? { ...current, comment } : current))
              }
              onSave={onSave}
              onSaveAndRestart={onSaveAndRestart}
            />
          </Paper>
        </Box>

        {/* Version info section */}
        <Box sx={{ flex: 2 }}>
          {infoPanel}
          <ConfigVersionInfo configVersion={configVersion} controllerId={controllerId} />
          {configVersion.previousVersionId && (
            <ConfigVersionNavigationLinks
              configVersionId={configVersion.id}
              controllerId={controllerId}
              previousVersionId={configVersion.previousVersionId}
            />
          )}
        </Box>
      </Stack>
    </Box>
  )
}
